import type { UncastObject } from './common'
import type { LOGIC_ACTION, LOGIC_OPERATOR } from './enums'
import type { BlockId, LogicId, StepId } from './ids'
import type { BlockReference, ContextParameterReference } from './references'

export enum LOGIC_TRIGGER {
  /** Triggers when a block state changes */
  VALUE_CHANGED = 'on-value-changed',
  /** Triggers when the "next" button is clicked in the Action Bar block */
  NEXT_CLICKED = 'on-click-next'
}

/**
 * Once evaluated, logics will output "events" with additional details that will then inform
 * the Journey App about how to execute the desired action. Those events are going to be implemented
 * according to each Action type and handled by their specific executors.
 */
export interface ILogic {
  readonly id: LogicId
  /**
   * Indicates whether this logic is protected or not.
   *
   * Protected logics can't be removed by the Configuring user as they're exclusively managed by the system,
   * in accordance with the Journey config.
   */
  protected?: boolean
  /** Which action should be taken */
  action: LOGIC_ACTION
  /** When should this logic be evaluated/triggered */
  triggeredOn: LOGIC_TRIGGER
  /**
   * List of logic expressions that must evalutate to "truthy" for the "action" to be taken.
   *
   * These are expressed in "disjunctive normal form" (DNF), whereas the first dimension means `OR` and the second dimension means `AND`,
   * therefore resulting in `[[AND] OR [AND] OR ...]`.
   *
   * @example
   * ```js
   * const conditions = [[a==1], [b==2]] // which represents, a == 1 OR b == 2
   * const conditions = [[a==1, b==2]] // which represents, a == 1 AND b == 2
   * ```
   *
   * @see https://en.wikipedia.org/wiki/Disjunctive_normal_form
   */
  conditions: Clause[][]
  /** Logic-specific settings, if any */
  settings?: Record<string, unknown>
}

interface IClause {
  /** Which is the logical operator, according to the value types */
  operator: LOGIC_OPERATOR
  /**
   * Unique identifier of the logic function.
   *
   * This is `undefined` for basic clauses, but required for function clauses.
   *
   * Tip: To reduce the likelihood of collisions, it is recommended to separate functions in different
   * "namespaces" (i.e. `namespace.name`) according to some common characteristics.
   */
  functionName?: string | never
  /**
   * The first operand of the logical expression.
   *
   * This can be a reference to a block, a context parameter or a static JSON XPath expression.
   */
  fact: BlockFact | ContextParameterFact | Fact
  /**
   * The right operand of the logical expression.
   *
   * This can be a static value of various types (string, number, boolean, null, undefined, object)
   * or a list of those values.
   */
  value: ComparisonValue
  /**
   * Additional arguments provided to the operator at runtime.
   * This is usually `undefined` for basic clauses, but optional for function clauses.
   */
  args?: UncastObject | never
}

export interface BasicClause extends IClause {
  /** Which is the logical operator, according to the value types */
  operator: Exclude<LOGIC_OPERATOR, LOGIC_OPERATOR.CUSTOM_FUNCTION>
  functionName?: never
}

/**
 * Function clauses are specified during runtime and can perform async operations.
 *
 * This is useful for performing async operations such as calling an API to perform a custom
 * validation off of a third-party service.
 */
export interface FunctionClause extends IClause {
  operator: LOGIC_OPERATOR.CUSTOM_FUNCTION
  functionName: string
  args?: UncastObject
}

export type Clause = BasicClause | FunctionClause

/**
 * A JSON XPath expression that indicates where the fact value is located on the given object,
 * which is then evaluated in runtime through {@link [jsonpath-plus](https://www.npmjs.com/package/jsonpath-plus)}.
 *
 * For additional information on XPath syntax, please check {@link [RFC 9535](https://www.rfc-editor.org/rfc/rfc9535#tbl-overview)}.
 */
export type Fact = string

export interface PathableFact {
  /**
   * Indicates from which path to get the fact value from the source object.
   */
  path?: Fact
}

export interface BlockFact extends BlockReference, PathableFact {
  meaning: 'data source'
}

export interface ContextParameterFact extends ContextParameterReference {
  meaning: 'data source'
}

export type ComparisonValuePrimitive =
  | string
  | number
  | boolean
  | null
  | undefined
  | {
      [key: string]: ComparisonValuePrimitive
    }

export type ComparisonValue =
  | ComparisonValuePrimitive
  | ComparisonValuePrimitive[]

export interface JumpToStepLogic extends ILogic {
  action: LOGIC_ACTION.JUMP_TO_STEP
  triggeredOn: LOGIC_TRIGGER.VALUE_CHANGED | LOGIC_TRIGGER.NEXT_CLICKED
  settings: {
    /** Which Step we should jump to */
    targetStep: StepId
  }
}

export interface SkipStepLogic extends ILogic {
  action: LOGIC_ACTION.SKIP_STEP
  triggeredOn: LOGIC_TRIGGER.VALUE_CHANGED | LOGIC_TRIGGER.NEXT_CLICKED
  settings: {
    /** Which Steps should be skipped */
    skipSteps: StepId[]
  }
}

export interface DisplayConditionLogic extends ILogic {
  action: LOGIC_ACTION.DISPLAY_CONDITION
  triggeredOn: LOGIC_TRIGGER.VALUE_CHANGED
  settings: {
    /** Which block should be controlled by this condition */
    targetBlock: BlockId
    /**
     * Which visibility action should be performed
     * @see {@link https://e-pilot.atlassian.net/browse/ER-4511}
     */
    visibilityAction: 'show' | 'hide'
  }
}

export interface DataInjectionLogic extends ILogic {
  action: LOGIC_ACTION.DATA_INJECTION
  triggeredOn: LOGIC_TRIGGER.VALUE_CHANGED
  settings: {
    /** From which block the data is coming from */
    sourceBlock: BlockId
    /** To which block the data is going to */
    targetBlock: BlockId
  }
}

export type Logic =
  | JumpToStepLogic
  | SkipStepLogic
  | DisplayConditionLogic
  | DataInjectionLogic
